import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CampaignsApiContext,
  InProgressFormModelFormType,
  ProposalCampaignConversionErrorCode,
  useConvertProposalCampaign,
  useInvalidateUseGetCampaign,
} from '@urbanx/agx.campaigns.client';
import { AgxAnimatedToggleLoader, AgxButton, AgxTypography } from '@urbanx/agx.ui-components2';
import { classifyConversionError, type ConversionFailure } from './conversionErrors';
import * as styles from './AgreementReadinessDialog.css';

export type OpenAgreement = (agreement: { campaignId: string; formId: string }) => void;

// Dialogs render outside the router, so the route that opens readiness supplies this.
// The identity stays in the URL so a reload restores the agreement form.
export const useOpenAgreement = (): OpenAgreement => {
  const navigate = useNavigate();
  return (agreement) => void navigate(`/form?${new URLSearchParams(agreement)}`);
};

type Attempt = {
  // Omitted when the source form must be found on the campaign.
  formId?: string;
  expectedETag?: string;
  // Follow a SourceFormConflict to the form that created the existing agreement.
  followSource?: boolean;
};

type ConversionState =
  | { status: 'idle' }
  | { status: 'confirming' }
  | { status: 'pending' }
  | { status: 'failed'; failure: ConversionFailure }
  | { status: 'notDraft' };

const noSourceForm: ConversionFailure = {
  message:
    'No saved proposal draft is available to find the agreement. Continue from the campaign on the dashboard.',
  next: 'none',
};

const useAgreementConversion = ({
  campaignId,
  openAgreement,
  close,
}: {
  campaignId: string;
  openAgreement: OpenAgreement;
  close: () => void;
}) => {
  const campaigns = useContext(CampaignsApiContext);
  const invalidateCampaign = useInvalidateUseGetCampaign();
  const { convertProposalCampaign } = useConvertProposalCampaign();
  const [state, setState] = useState<ConversionState>({ status: 'idle' });
  const inFlight = useRef(false);
  const lastAttempt = useRef<Attempt>({});

  const sourceFormId = async (attempt: Attempt) => {
    if (attempt.formId) return attempt.formId;
    const campaign = await campaigns.getCampaign(campaignId);
    return campaign.inProgressForms?.find(
      (form) => form.formType === InProgressFormModelFormType.ListingProposal && form.formId
    )?.formId;
  };

  const convert = async (formId: string, attempt: Attempt) => {
    try {
      return await convertProposalCampaign({
        campaignId,
        formId,
        expectedETag: attempt.expectedETag,
      });
    } catch (error) {
      const failure = classifyConversionError(error);
      if (
        !attempt.followSource ||
        failure.code !== ProposalCampaignConversionErrorCode.SourceFormConflict ||
        !failure.sourceFormId ||
        failure.sourceFormId === formId
      )
        throw error;
      return convertProposalCampaign({ campaignId, formId: failure.sourceFormId });
    }
  };

  const run = async (attempt: Attempt) => {
    // State updates are not synchronous, so a ref stops a second click sending a request.
    if (inFlight.current) return;
    inFlight.current = true;
    lastAttempt.current = attempt;
    setState({ status: 'pending' });
    try {
      const formId = await sourceFormId(attempt);
      if (!formId) {
        setState({ status: 'failed', failure: noSourceForm });
        return;
      }
      const response = await convert(formId, attempt);
      void invalidateCampaign(campaignId);
      if (!response.agreementDraftAvailable) {
        setState({ status: 'notDraft' });
        return;
      }
      close();
      openAgreement({ campaignId: response.campaignId, formId: response.agreementFormId });
    } catch (error) {
      setState({ status: 'failed', failure: classifyConversionError(error) });
    } finally {
      inFlight.current = false;
    }
  };

  return {
    state,
    confirm: () => setState({ status: 'confirming' }),
    cancel: () => setState({ status: 'idle' }),
    run,
    retry: () => run(lastAttempt.current),
  };
};

type Conversion = ReturnType<typeof useAgreementConversion>;

const ConversionStatus = ({
  conversion,
  pendingLabel,
  onReviewAgain,
}: {
  conversion: Conversion;
  pendingLabel: string;
  onReviewAgain?: () => void;
}) => {
  const { state } = conversion;
  if (state.status === 'pending')
    return (
      <div role="status">
        <AgxAnimatedToggleLoader label={pendingLabel} />
      </div>
    );
  if (state.status === 'notDraft')
    return (
      <AgxTypography id="agreement-conversion-not-draft" type="body" size="small" role="alert">
        This campaign’s agreement is no longer a draft. Continue from the campaign on the dashboard.
      </AgxTypography>
    );
  if (state.status !== 'failed') return null;
  const { failure } = state;
  return (
    <>
      <AgxTypography id="agreement-conversion-error" type="body" size="small" role="alert">
        {failure.message}
      </AgxTypography>
      {failure.next === 'retry' && (
        <AgxButton
          id="agreement-conversion-retry"
          text="Retry"
          variant="hollow"
          onClick={() => void conversion.retry()}
        />
      )}
      {failure.next === 'reviewAgain' && onReviewAgain && (
        <AgxButton
          id="agreement-conversion-review-again"
          text="Review again"
          variant="hollow"
          onClick={onReviewAgain}
        />
      )}
      {failure.next === 'openExisting' && failure.sourceFormId && (
        <AgxButton
          id="agreement-conversion-open-existing"
          text="Open agreement draft"
          variant="primary"
          onClick={() => void conversion.run({ formId: failure.sourceFormId })}
        />
      )}
    </>
  );
};

// Creates the agreement draft from a reviewed proposal. Readiness is advisory: missing
// categories never block conversion, and remaining work shows in the agreement form.
export const AgreementConversion = ({
  campaignId,
  formId,
  campaignETag,
  onReviewAgain,
  openAgreement,
  close,
}: {
  campaignId: string;
  formId?: string;
  campaignETag: string;
  onReviewAgain: () => void;
  openAgreement: OpenAgreement;
  close: () => void;
}) => {
  const conversion = useAgreementConversion({ campaignId, openAgreement, close });
  const { state } = conversion;
  if (!formId)
    return (
      <AgxTypography id="agreement-conversion-no-form" type="body" size="small">
        Save the proposal’s agreement details before creating an agreement draft.
      </AgxTypography>
    );
  return (
    <section
      className={styles.conversion}
      data-testid="agreement-conversion"
      aria-label="Create agreement draft"
    >
      {state.status === 'idle' && (
        <AgxButton
          id="agreement-conversion-start"
          text="Create agreement draft"
          variant="primary"
          onClick={conversion.confirm}
        />
      )}
      {state.status === 'confirming' && (
        <>
          <AgxTypography id="agreement-conversion-confirmation" type="body" size="small">
            Create an agreement draft from this proposal’s saved details? You’ll continue in the
            agreement form, where any remaining details are highlighted. The proposal and its shared
            versions won’t change.
          </AgxTypography>
          <div className={styles.conversionActions}>
            <AgxButton
              id="agreement-conversion-confirm"
              text="Confirm and create"
              variant="primary"
              onClick={() => void conversion.run({ formId, expectedETag: campaignETag })}
            />
            <AgxButton
              id="agreement-conversion-cancel"
              text="Cancel"
              variant="hollow"
              onClick={conversion.cancel}
            />
          </div>
        </>
      )}
      <ConversionStatus
        conversion={conversion}
        pendingLabel="Creating agreement draft…"
        onReviewAgain={onReviewAgain}
      />
    </section>
  );
};

// Opens the agreement for a campaign that has already been converted. Asking the
// conversion API again returns the existing draft and finishes any pending setup.
export const ExistingAgreement = ({
  campaignId,
  formId,
  openAgreement,
  close,
}: {
  campaignId: string;
  formId?: string;
  openAgreement: OpenAgreement;
  close: () => void;
}) => {
  const conversion = useAgreementConversion({ campaignId, openAgreement, close });
  return (
    <section className={styles.conversion} data-testid="agreement-conversion">
      {conversion.state.status === 'idle' && (
        <AgxButton
          id="agreement-conversion-open"
          text="Open agreement draft"
          variant="primary"
          onClick={() => void conversion.run({ formId, followSource: true })}
        />
      )}
      <ConversionStatus conversion={conversion} pendingLabel="Opening agreement draft…" />
    </section>
  );
};
